import React, { useEffect, useState, useCallback } from 'react';
import { range12 } from '../lib/timeFormat';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GraduationCap, Video, Clock, Check, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'TBD';

export default function InductionApprovalPage() {
  const { inductionId, sessionId } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(null); // reject | reschedule
  const [reason, setReason] = useState('');
  const [suggested, setSuggested] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/induction-approval/${inductionId}/${sessionId}?token=${encodeURIComponent(token || '')}`);
    if (r.ok) setData(await r.json());
    else { const d = await r.json().catch(() => ({})); setError(d.detail || 'Invalid link'); }
    setLoading(false);
  }, [inductionId, sessionId, token]);

  useEffect(() => { load(); }, [load]);

  const respond = async (action) => {
    if (action === 'reject' && !reason.trim()) { toast.error('Please provide a reason'); return; }
    if (action === 'reschedule' && !suggested.trim()) { toast.error('Please suggest a time'); return; }
    setSubmitting(true);
    const r = await fetch(`${API}/induction-approval/${inductionId}/${sessionId}/respond?token=${encodeURIComponent(token || '')}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, reason, suggested_time: suggested }),
    });
    setSubmitting(false);
    if (r.ok) { toast.success('Response recorded. Thank you!'); setMode(null); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
        <X className="h-10 w-10 mx-auto text-rose-500 mb-3" /><p className="font-medium">{error}</p>
        <p className="text-sm text-muted-foreground mt-1">This approval link is invalid or has expired.</p>
      </CardContent></Card>
    </div>
  );

  const s = data.session;
  const decided = s.approval_status && s.approval_status !== 'Pending';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full" data-testid="approval-page">
        <CardHeader>
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1"><GraduationCap className="h-4 w-4" /> Induction Session Confirmation</div>
          <CardTitle className="text-xl">{s.name}</CardTitle>
          <p className="text-sm text-muted-foreground">Please confirm the timing for your session below.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 text-sm bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /> {fmtDate(s.date)} {s.start_time && `· ${range12(s.start_time, s.end_time)}`}</div>
            {data.online_url && <div className="flex items-center gap-2"><Video className="h-4 w-4 text-muted-foreground" /> <a href={data.online_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Meeting link</a></div>}
          </div>

          {(data.joiners || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Joiners ({data.joiners.length})</p>
              <div className="flex flex-wrap gap-2" data-testid="approval-joiners">
                {data.joiners.map((j, i) => <Badge key={i} variant="secondary" className="py-1">{j.name} · {j.mode}{j.mode === 'Offline' && j.location ? ` (${j.location})` : ''}</Badge>)}
              </div>
            </div>
          )}

          {(data.same_day_sessions || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Also scheduled that day</p>
              <div className="space-y-1">
                {data.same_day_sessions.map((o, i) => <div key={i} className="text-sm text-muted-foreground flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> {o.start_time ? range12(o.start_time, o.end_time) : '--:--'} · {o.name}</div>)}
              </div>
            </div>
          )}

          {decided ? (
            <div className="text-center py-4" data-testid="approval-decided">
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
              <p>You responded: <Badge>{s.approval_status}</Badge></p>
              {s.reject_reason && <p className="text-sm text-rose-600 mt-2">Reason: {s.reject_reason}</p>}
              {s.suggested_time && <p className="text-sm text-amber-600 mt-2">Suggested: {s.suggested_time}</p>}
              <p className="text-xs text-muted-foreground mt-3">You can change your response below if needed.</p>
              <div className="flex gap-2 justify-center mt-3">
                <Button size="sm" variant="outline" onClick={() => respond('accept')} data-testid="approval-accept-btn"><Check className="h-4 w-4 mr-1" /> Accept</Button>
                <Button size="sm" variant="outline" onClick={() => setMode('reject')}>Reject</Button>
              </div>
            </div>
          ) : mode === 'reject' ? (
            <div className="space-y-3" data-testid="approval-reject-form">
              <Label>Reason for rejection *</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Why can't you take this session?" data-testid="reject-reason-input" />
              <Label>Suggested time (optional)</Label>
              <Input type="datetime-local" value={suggested} onChange={e => setSuggested(e.target.value)} data-testid="reject-suggest-input" />
              <div className="flex gap-2"><Button variant="destructive" disabled={submitting} onClick={() => respond('reject')} data-testid="submit-reject-btn">Submit Rejection</Button><Button variant="ghost" onClick={() => setMode(null)}>Cancel</Button></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="approval-actions">
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting} onClick={() => respond('accept')} data-testid="approval-accept-btn"><Check className="h-4 w-4 mr-1" /> Accept</Button>
              <Button variant="outline" onClick={() => setMode('reject')} data-testid="approval-reject-btn"><X className="h-4 w-4 mr-1" /> Reject</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
